import { useCallback, useState } from "react";
import { handleCreateModel } from "@/components/main/utils/models/ModelServices";
import { useMainStateStore } from "@/components/main/stores/mainStateStore";
import { useTeamsStore } from "@/components/main/stores/teamsStore";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";

interface UseCreateModelOptions {
  /// When set, the model is created inside this team and added to its list.
  teamId?: string;
}

interface UseCreateModel {
  /// Id of the most recently created model, so the list can play the settle-in
  /// `overview-fresh` animation on that row (`isFresh` on `ModelRow`).
  freshModelId: string | null;
  /// True while a create request is in flight (guards double-submits).
  isCreating: boolean;
  /// `catalog` creates a role-owned catalog model instead of a personal one —
  /// the server rejects it without `can_manage_public_catalog`.
  createModel: (options?: { catalog?: boolean }) => Promise<void>;
}

/// Shared "create a model" action for the Overview and Team model lists. Creates
/// the model, adds it to the relevant store(s), and marks it fresh so both lists
/// play the identical settle-in animation on the new row — one behavior, one
/// look, whether the model is created on the overview or inside a team.
export function useCreateModel({
  teamId,
}: UseCreateModelOptions = {}): UseCreateModel {
  const [freshModelId, setFreshModelId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const addModel = useMainStateStore((s) => s.addModel);
  const addModelToTeam = useTeamsStore((s) => s.addModelToTeam);
  const fetchTeamModels = useTeamsStore((s) => s.fetchTeamModels);

  const createModel = useCallback(
    async ({ catalog = false }: { catalog?: boolean } = {}) => {
      if (isCreating) return;
      setIsCreating(true);
      try {
        const spaceId =
          !teamId && !catalog
            ? useOrgContextStore.getState().createSpaceId()
            : null;
        // The door refuses a scheduled space, and its 409 would surface as the
        // generic failure toast — say which space and why instead.
        const target = useOrgContextStore.getState().spaceById(spaceId);
        if (target?.deletion_scheduled_at) {
          showNotificationGlobal(
            `${target.name} is scheduled for deletion — it takes no new models until that is cancelled`,
            "error",
          );
          return;
        }
        const created = await handleCreateModel({
          ...(teamId ? { team_id: teamId } : {}),
          ...(catalog ? { is_catalog: true } : {}),
          ...(spaceId ? { space_id: spaceId } : {}),
        });
        if (!created) {
          showNotificationGlobal("Failed to create model", "error");
          return;
        }
        if (teamId) {
          // Team models carry the caller's access level; default to Viewer until
          // the refetch below returns the authoritative value.
          const normalized = {
            ...created,
            team_access: created.team_access || "Viewer",
          };
          addModel(normalized);
          addModelToTeam(teamId, normalized);
          void fetchTeamModels(teamId, true, false, true);
          setFreshModelId(String(normalized.model_id));
        } else {
          addModel(created);
          setFreshModelId(String(created.model_id));
        }
      } catch {
        showNotificationGlobal("Failed to create model", "error");
      } finally {
        setIsCreating(false);
      }
    },
    [isCreating, teamId, addModel, addModelToTeam, fetchTeamModels],
  );

  return { freshModelId, isCreating, createModel };
}
